class PreOrderManager {
    constructor() {
        this.apiQuery = `
      query GetProducts($products: [Object]) {
        getProducts(products: $products) {
          data {
            label
            customMessage
            products{
              productId
              handle
              variants {
                title
                variantId
                sku
                available
                config
              }
            }
          }
          meta {
            message
            statusCode
          }
        }
      }
    `;

        this.cache = new Map();
        this.preorderVariantIds = new Set();
        this.init();
    }

    isValidateForPreOrder(config) {
        if (!config || !config.start_date || !config.end_date) return false;

        const date = new Date();
        let dateFrom = config.start_date.split("T")[0].replace(/-/g, "/");
        let currnetDate = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;

        let dateFromParse = dateFrom.split("/");
        let currentDateParse = currnetDate.split("/");

        let from = new Date(
            dateFromParse[0],
            parseInt(dateFromParse[1]) - 1,
            dateFromParse[2]
        );
        let check = new Date(
            currentDateParse[2],
            parseInt(currentDateParse[1]) - 1,
            currentDateParse[0]
        );

        return check < from && config.action_name == "Preorder";
    }

    async fetchProducts(products) {
        // Basic deduplication & caching
        const uncached = products.filter(p => !this.cache.has(p.productId));
        if (uncached.length > 0) {
            try {
                const req = await fetch(`https://ship.heirloomroses.com/gql`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "accept": "application/json",
                    },
                    body: JSON.stringify({
                        query: this.apiQuery,
                        variables: { products: uncached },
                    }),
                });
                const res = await req.json();
                const fetchedProducts = res?.data?.getProducts?.data?.products || [];

                let label = res?.data?.getProducts?.data?.label || "Pre Order";
                let customMessage = res?.data?.getProducts?.data?.customMessage || "";

                fetchedProducts.forEach(fp => {
                    this.cache.set(fp.productId, {
                        data: fp,
                        label: label,
                        customMessage: customMessage
                    });
                });
            } catch (e) {
                console.error("PreOrderManager Error:", e);
            }
        }

        return products.map(p => this.cache.get(p.productId)).filter(Boolean);
    }

    async processProductForms(forms) {
        if (forms.length === 0) return;

        let payload = Array.from(forms).map(form => {
            let productId = form.dataset.productId;
            if (!productId) return null;

            const dataScript = document.getElementById(`preorder-data-${productId}`);
            if (dataScript) {
                try {
                    return JSON.parse(dataScript.textContent);
                } catch (e) {
                    console.error("Failed to parse preorder data", e);
                }
            }

            // Fallback
            return { productId: productId, handle: "", variants: [{ variantId: "1" }] };
        }).filter(Boolean);

        // Filter duplicates
        payload = Array.from(new Map(payload.map(item => [item.productId, item])).values());

        const apiResults = await this.fetchProducts(payload);

        forms.forEach(form => {
            let productId = form.dataset.productId;
            if (!productId) return;

            const apiResult = apiResults.find(r => r.data.productId === productId);
            if (!apiResult) return;

            const idInput = form.querySelector('input[name="id"]');
            if (!idInput) return;

            const currentVariantId = idInput.value;
            const variantConfig = apiResult.data.variants.find(v => v.variantId === currentVariantId);

            this.updateUI(form, variantConfig?.config, apiResult.label, apiResult.customMessage);
        });
    }

    updateUI(form, config, label, customMessage) {
        const actionLabel = (config && config.action_name ? config.action_name : (label || "")).toString();
        const actionLabelNorm = actionLabel.trim().toLowerCase();
        const isPreOrderLabel =
            actionLabelNorm === "preorder" ||
            actionLabelNorm === "pre order" ||
            actionLabelNorm === "pre-order";

        const isPreOrder = this.isValidateForPreOrder(config) || isPreOrderLabel;

        const atcButtonContainer = form.querySelector('add-to-cart-button') || form.querySelector('button[name="add"]')?.closest('add-to-cart-button');
        const atcButtonLabel = form.querySelector('.add-to-cart-text__content > span > span');

        // Hidden Input logic
        let hiddenInput = form.querySelector('input#pre-order-message');

        const currentVariantId = form.querySelector('input[name="id"]')?.value;
        if (currentVariantId) {
            if (isPreOrder) this.preorderVariantIds.add(currentVariantId);
            else this.preorderVariantIds.delete(currentVariantId);
        }

        // Message Container (Product Page specific)
        const container = form.closest('.shopify-section')?.querySelector('.pre-order-container');

        // Sticky Mobile Button (Product Page specific)
        const stickyButtonContainer = form.closest('.shopify-section')?.querySelector('sticky-add-to-cart');
        const stickyButtonLabel = stickyButtonContainer?.querySelector('.add-to-cart-text__content > span:first-child');
        const stickyButtonNode = stickyButtonContainer?.querySelector('button');

        if (isPreOrder) {
            if (atcButtonContainer && typeof atcButtonContainer.enable === 'function') {
                atcButtonContainer.enable();
            } else if (form.querySelector('button[name="add"]')) {
                form.querySelector('button[name="add"]').disabled = false;
            }

            if (atcButtonLabel) {
                atcButtonLabel.dataset.originalText = atcButtonLabel.dataset.originalText || atcButtonLabel.innerHTML;
                atcButtonLabel.innerHTML = (config && config.action_name) || label || "Pre Order";
            }

            if (stickyButtonLabel) {
                stickyButtonLabel.dataset.originalText = stickyButtonLabel.dataset.originalText || stickyButtonLabel.innerHTML;
                stickyButtonLabel.innerHTML = (config && config.action_name) || label || "Pre Order";
            }

            if (stickyButtonNode) {
                stickyButtonNode.disabled = false;
            }

            // Match legacy behavior: insert property input at form start
            if (!hiddenInput) {
                form.insertAdjacentHTML(
                    'afterbegin',
                    "<input type='hidden' name='properties[Message]' value='This is a pre-order item' id='pre-order-message'>"
                );
                hiddenInput = form.querySelector('input#pre-order-message');
            } else {
                hiddenInput.value = 'This is a pre-order item';
            }

            if (container) {
                let messageText =
                    (config && config.custom_message && config.custom_message.trim() !== '')
                        ? config.custom_message
                        : customMessage;
                let preOrderIcon = `
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" focusable="false" aria-hidden="true" class="infoIcon"><g clip-path="url(#a)"><circle cx="7" cy="7" r="5.5"></circle><path stroke-linejoin="round" d="M6.99 4.49h.02v.02h-.02z"></path><path stroke-linecap="round" stroke-linejoin="round" d="M7 9.75v-3"></path></g><defs><clipPath id="a"><path fill="#fff" d="M0 0h14v14H0z"></path></clipPath></defs></svg>
                `;

                container.innerHTML = `
                  <custom-app-message class="pre-order-product">
                    ${preOrderIcon}
                    <span class="pre-order_message-text enlarge-text">${messageText}</span>
                  </custom-app-message>
                `;
                container.classList.remove('empty');
            }

        } else {
            // Revert Pre-order specifics Native ThemeEvents.variantUpdate handles standard sold-out disabling.
            if (atcButtonLabel && atcButtonLabel.dataset.originalText) {
                atcButtonLabel.innerHTML = atcButtonLabel.dataset.originalText;
            }

            if (hiddenInput) hiddenInput.remove();

            if (container) {
                container.innerHTML = '';
                container.classList.add('empty');
            }

            if (stickyButtonLabel && stickyButtonLabel.dataset.originalText) {
                stickyButtonLabel.innerHTML = stickyButtonLabel.dataset.originalText;
            }
        }
    }

    init() {
        // 1. Initial Page Load (Product Pages)
        const forms = document.querySelectorAll('product-form-component');
        this.processProductForms(forms);

        // 2. Listen to Variant Updates (Horizon Native)
        document.addEventListener('variantUpdate', (evt) => {
            // The event is fired from variant-picker, but product-form-component handles the UI updates natively.
            // We wait a tiny tick for Horizon to finish its native UI updates (like disabling the button), then we override.
            setTimeout(() => {
                const form = evt.target.closest('.shopify-section')?.querySelector('product-form-component');
                if (form) {
                    this.processProductForms([form]);
                }
            }, 50);
        });

        // 3. Fallback for Quick Add buttons directly injected without <product-form-component> (if any)
        const quickAddButtons = document.querySelectorAll('.quick-add__button--add');
        if (quickAddButtons.length > 0) {
            const qForms = document.querySelectorAll('quick-add-component product-form-component');
            this.processProductForms(qForms);
        }

        // 4. Global Cart Hook Fallback: Ensure pre-order properties are attached
        this.hookCartAdd();

        // 5. Final fallback: append property right before form submit
        document.addEventListener('submit', (evt) => {
            const form = evt.target;
            if (!(form instanceof HTMLFormElement)) return;
            if (!form.action || !form.action.includes('/cart/add')) return;

            const variantId = form.querySelector('input[name="id"]')?.value;
            const isMarkedPreorder = Boolean(form.querySelector('#pre-order-message'));
            if (!variantId) return;
            if (!isMarkedPreorder && !this.preorderVariantIds.has(String(variantId))) return;

            let input = form.querySelector('input[name="properties[Message]"]');
            if (!input) {
                input = document.createElement('input');
                input.type = 'hidden';
                input.name = 'properties[Message]';
                form.appendChild(input);
            }
            input.value = 'This is a pre-order item';
        }, true);
    }

    hookCartAdd() {
        const originalFetch = window.fetch;
        const manager = this;
        window.fetch = async function (...fetchArgs) {
            let args = fetchArgs;
            const requestUrl = (() => {
                const target = args[0];
                if (typeof target === 'string') return target;
                if (target && typeof target.url === 'string') return target.url;
                if (target && typeof target.href === 'string') return target.href;
                return '';
            })();

            if (requestUrl.includes('/cart/add')) {
                try {
                    // Standardize arguments
                    let options = args[1] || {};
                    const body = options.body;

                    // JSON payload (application/json)
                    if (body && typeof body === 'string') {
                        let data = JSON.parse(body);
                        if (data && data.id) {
                            const idStr = String(data.id);
                            if (manager.preorderVariantIds.has(idStr)) {
                                data.properties = data.properties || {};
                                if (!data.properties.Message) data.properties.Message = 'This is a pre-order item';
                                options.body = JSON.stringify(data);
                                args[1] = options;
                            }
                        } else if (data && Array.isArray(data.items)) {
                            data.items = data.items.map((it) => {
                                const idStr = it && it.id != null ? String(it.id) : '';
                                if (idStr && manager.preorderVariantIds.has(idStr)) {
                                    it.properties = it.properties || {};
                                    if (!it.properties.Message) it.properties.Message = 'This is a pre-order item';
                                }
                                return it;
                            });
                            options.body = JSON.stringify(data);
                            args[1] = options;
                        }
                    }

                    // FormData payload (most themes)
                    if (body instanceof FormData) {
                        // Single item form: id=VARIANT_ID
                        const id = body.get('id');
                        const idStr = id ? String(id) : '';
                        const domMarksPreorder = (() => {
                            if (!idStr) return false;
                            const formWithVariant = document.querySelector(`product-form-component input[name="id"][value="${CSS.escape(idStr)}"]`)?.closest('product-form-component');
                            return Boolean(formWithVariant?.querySelector('#pre-order-message'));
                        })();

                        if (id && (manager.preorderVariantIds.has(idStr) || domMarksPreorder)) {
                            if (!body.get('properties[Message]')) {
                                body.append('properties[Message]', 'This is a pre-order item');
                            }
                        }

                        // Multi-item add: items[0][id], items[1][id], ...
                        for (let i = 0; i < 50; i++) {
                            const itemIdKey = `items[${i}][id]`;
                            if (!body.has(itemIdKey)) break;
                            const itemId = body.get(itemIdKey);
                            if (itemId && manager.preorderVariantIds.has(String(itemId))) {
                                const propKey = `items[${i}][properties][Message]`;
                                if (!body.get(propKey)) {
                                    body.append(propKey, 'This is a pre-order item');
                                }
                            }
                        }

                        options.body = body;
                        args[1] = options;
                    }
                } catch (e) { }
            }
            return originalFetch.apply(this, args);
        };
    }
}

document.addEventListener('DOMContentLoaded', () => {
    window.themePreOrderManager = new PreOrderManager();
});
